import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, ExternalLink, Loader2, Lock } from "lucide-react";
import { api, publicUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ClassicPreview = () => (
  <div className="rounded-lg overflow-hidden border border-[#E3E1DA] bg-[#F4F3EF] h-32" aria-hidden="true">
    <div className="h-5 bg-white border-b border-[#E3E1DA] flex items-center gap-1.5 px-2">
      <span className="h-1.5 w-6 rounded bg-[#2E7D72]" /><span className="h-1 w-4 rounded bg-[#0F2A44]/30" /><span className="h-1 w-4 rounded bg-[#0F2A44]/30" /><span className="h-1 w-4 rounded bg-[#0F2A44]/30" />
    </div>
    <div className="p-2.5">
      <span className="block h-2 w-16 rounded bg-[#0F2A44]" />
      <div className="grid grid-cols-3 gap-1.5 mt-2.5">{[0, 1, 2].map((i) => <span key={i} className="h-9 rounded-md bg-white border border-[#E3E1DA] flex items-end p-1"><span className="h-1.5 w-4 rounded bg-[#2E7D72]" /></span>)}</div>
      <span className="block h-8 rounded-md bg-white border border-[#E3E1DA] mt-1.5" />
    </div>
  </div>
);

const StudioPreview = () => (
  <div className="rounded-lg overflow-hidden border border-[#181818] bg-[#F5F3E9] h-32 flex" aria-hidden="true">
    <div className="w-12 bg-[#181818] p-1.5 space-y-1.5">
      <span className="block h-1.5 w-7 rounded bg-[#B4E500]" />
      <span className="block h-2.5 rounded bg-[#B4E500] mt-3" />
      {[0, 1, 2].map((i) => <span key={i} className="block h-1.5 w-7 rounded bg-white/25" />)}
    </div>
    <div className="flex-1 p-2.5">
      <span className="block h-3 w-20 rounded-sm bg-[#181818]" />
      <div className="grid grid-cols-3 gap-1.5 mt-2.5">
        {["#B4E500", "#EFA4C5", "#A9D4F5"].map((c) => <span key={c} className="h-9 rounded-md border border-[#181818]" style={{ background: c }} />)}
      </div>
      <span className="block h-8 rounded-md bg-[#FFFEF9] border border-[#DEDACB] mt-1.5" />
    </div>
  </div>
);

const ThemeCard = ({ id, name, desc, label, preview, active, children }) => (
  <div className={`rounded-xl border p-4 flex flex-col ${active ? "border-navy ring-2 ring-black/10" : "border-line"}`} data-testid={`theme-card-${id}`}>
    {preview}
    <div className="flex items-center justify-between gap-2 mt-4">
      <p className="font-display text-navy font-semibold">{name}</p>
      <span className="text-[10px] uppercase tracking-[0.14em] font-semibold rounded-full px-2.5 py-1 bg-teal-soft text-teal" data-testid={`theme-label-${id}`}>{label}</span>
    </div>
    <p className="text-sm text-muted-ink mt-1 flex-1">{desc}</p>
    <div className="mt-4 flex flex-wrap items-center gap-y-2">{children}</div>
  </div>
);

export const ThemePicker = () => {
  const { church, setChurch } = useAuth();
  const t = church.dashboard_theme || { effective: "classic", can_use_studio: false };
  const [busy, setBusy] = useState(null);
  const choose = async (theme) => {
    setBusy(theme);
    try {
      const { data } = await api.put("/theme", { theme });
      setChurch({ ...church, dashboard_theme: data });
      toast.success(theme === "studio" ? "Studio theme applied" : "Classic theme applied");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change theme");
    } finally {
      setBusy(null);
    }
  };
  const btn = (theme, text) => t.effective === theme
    ? <span className="inline-flex items-center gap-1.5 text-sm font-medium text-teal" data-testid={`theme-active-${theme}`}><Check size={16} />Current theme</span>
    : <button type="button" onClick={() => choose(theme)} disabled={!!busy} className="btn-navy !py-2 !px-4 text-sm inline-flex items-center gap-2" data-testid={`use-${theme}-btn`}>
      {busy === theme && <Loader2 size={14} className="animate-spin" />}{text}</button>;
  return (
    <section className="card-soft p-8 md:p-10 mt-6" data-testid="appearance-section">
      <p className="eyebrow">Appearance</p>
      <p className="font-display text-navy text-lg font-semibold mt-2">Choose Your Dashboard Theme</p>
      <p className="text-sm text-muted-ink mt-1">Make your Dream Teams workspace feel like yours. Choose the dashboard experience that works best for you.</p>
      <div className="grid sm:grid-cols-2 gap-4 mt-6">
        <ThemeCard id="classic" name="Classic" desc="Existing Dream Teams dashboard." label="Default" preview={<ClassicPreview />} active={t.effective === "classic"}>
          {btn("classic", "Use Classic")}
        </ThemeCard>
        <ThemeCard id="studio" name="Studio" desc="Contemporary editorial dashboard." label="Pro Exclusive" preview={<StudioPreview />} active={t.effective === "studio"}>
          {t.can_use_studio ? btn("studio", "Use Studio") : (
            <Link to="/billing" className="btn-ghost !py-2 !px-4 text-sm inline-flex items-center gap-2" data-testid="theme-upgrade-btn"><Lock size={14} />Upgrade to Pro</Link>
          )}
        </ThemeCard>
      </div>
    </section>
  );
};

const AssessClassicPreview = () => (
  <div className="rounded-lg overflow-hidden border border-[#E3E1DA] bg-[#F4F3EF] h-32" aria-hidden="true">
    <div className="h-4 bg-white border-b border-[#E3E1DA]" /><div className="h-0.5 w-1/2 bg-[#2E7D72]" />
    <div className="px-6 py-2.5">
      <span className="block h-2 w-24 rounded bg-[#0F2A44]" />
      <div className="bg-white border border-[#E3E1DA] rounded-md mt-2 p-2">
        <span className="block h-1.5 w-28 rounded bg-[#0F2A44]/40" />
        <div className="grid grid-cols-5 gap-1 mt-2">{[0, 1, 2, 3, 4].map((i) => <span key={i} className={`h-4 rounded ${i === 3 ? "bg-[#0F2A44]" : "bg-white border border-[#E3E1DA]"}`} />)}</div>
      </div>
      <span className="block h-4 w-14 rounded bg-[#0F2A44] mt-2 ml-auto" />
    </div>
  </div>
);

const AssessStudioPreview = () => (
  <div className="rounded-lg overflow-hidden border border-[#181818] bg-[#F5F3E9] h-32" aria-hidden="true">
    <div className="h-4 bg-[#181818]" /><div className="h-1 w-1/2 bg-[#B4E500]" />
    <div className="px-6 py-2.5">
      <span className="block h-3 w-28 rounded-sm bg-[#181818]" />
      <div className="bg-[#FFFEF9] border border-[#181818] rounded-md mt-2 p-2" style={{ boxShadow: "3px 3px 0 #A9D4F5" }}>
        <span className="block h-1.5 w-28 rounded bg-[#181818]/50" />
        <div className="grid grid-cols-5 gap-1 mt-2">{[0, 1, 2, 3, 4].map((i) => <span key={i} className={`h-4 rounded border ${i === 3 ? "bg-[#B4E500] border-[#181818]" : "bg-[#FFFEF9] border-[#DEDACB]"}`} />)}</div>
      </div>
      <span className="block h-4 w-14 rounded-full bg-[#B4E500] border border-[#181818] mt-2 ml-auto" />
    </div>
  </div>
);

const PreviewLink = ({ slug, theme }) => (
  <a href={`${publicUrl(slug)}?preview=${theme}`} target="_blank" rel="noreferrer" className="text-sm text-navy underline underline-offset-4 inline-flex items-center gap-1 ml-4" data-testid={`public-preview-${theme}-link`}>
    Preview <ExternalLink size={13} />
  </a>
);

export const PublicThemePicker = () => {
  const { church, setChurch } = useAuth();
  const t = church.public_theme_state || { preference: "classic", effective: "classic", can_use_studio: false };
  const [busy, setBusy] = useState(null);
  const viewer = church.role === "viewer";
  const apply = async (theme) => {
    setBusy(theme);
    try {
      const { data } = await api.put("/theme/public", { theme });
      setChurch({ ...church, public_theme_state: data });
      toast.success(`Volunteers will now see the ${theme === "studio" ? "Studio" : "Classic"} assessment`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change the public theme");
    } finally {
      setBusy(null);
    }
  };
  const btn = (theme) => t.effective === theme
    ? <span className="inline-flex items-center gap-1.5 text-sm font-medium text-teal" data-testid={`public-theme-active-${theme}`}><Check size={16} />Live for volunteers</span>
    : !viewer && <button type="button" onClick={() => apply(theme)} disabled={!!busy} className="btn-navy !py-2 !px-4 text-sm inline-flex items-center gap-2" data-testid={`apply-public-${theme}-btn`}>
      {busy === theme && <Loader2 size={14} className="animate-spin" />}Apply</button>;
  return (
    <section className="card-soft p-8 md:p-10 mt-6" data-testid="public-theme-section">
      <p className="eyebrow">Appearance</p>
      <p className="font-display text-navy text-lg font-semibold mt-2">Public Assessment Theme</p>
      <p className="text-sm text-muted-ink mt-1">Choose the experience your volunteers see when they open your church's assessment link.</p>
      <div className="grid sm:grid-cols-2 gap-4 mt-6">
        <ThemeCard id="public-classic" name="Classic" desc="The current volunteer assessment design." label="Default" preview={<AssessClassicPreview />} active={t.effective === "classic"}>
          {btn("classic")}<PreviewLink slug={church.slug} theme="classic" />
        </ThemeCard>
        <ThemeCard id="public-studio" name="Studio" desc="The contemporary Dream Teams Studio design." label="Pro Exclusive" preview={<AssessStudioPreview />} active={t.effective === "studio"}>
          {t.can_use_studio ? btn("studio") : (
            <Link to="/billing" className="btn-ghost !py-2 !px-4 text-sm inline-flex items-center gap-2" data-testid="public-theme-upgrade-btn"><Lock size={14} />Upgrade to Pro</Link>
          )}<PreviewLink slug={church.slug} theme="studio" />
        </ThemeCard>
      </div>
      {!t.can_use_studio && t.preference === "studio" && <p className="text-xs text-muted-ink mt-4" data-testid="public-theme-saved-note">Your Studio choice is saved and will return when your church is on Pro.</p>}
    </section>
  );
};
